import { BaseModelPrivatization } from "./BaseModelPrivatization";
import { StaticDataPrivatizationType } from "./enum/StaticDataPrivatizationType";
import { Injectable } from "@angular/core";
import { SystemConfigPromiseService } from "shared/auth-services";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

const PRIVATE_ID = CubiqTS.Long.fromInt(-10000);
const RESTRICTED_ID = CubiqTS.Long.fromInt(-20000);
// branch private count down from -30000

@Injectable()
export class ClientTraderModelPrivatization extends BaseModelPrivatization<
  CubiqTS.Long,
  string,
  CubiqTS.UserImpl
> {
  getBranchPrivateLabel(branchCd: string): string {
    return `PRIVATE - ${branchCd}`;
  }
  getPrivateLabel(): string {
    return this.privateMask;
  }
  getRestrictedLabel(): string {
    return "Restricted";
  }
  getPrefixPrivate(): string {
    return this.prefix + "_private";
  }
  getPrefixRestricted(): string {
    return this.prefix + "_restricted";
  }

  private privateMask = "-PRIVATE-";
  private prefix = "__bfx_privatization";
  private nextBranchCdId = -30000;
  private branchCdIdMap = new Map<string, CubiqTS.Long>();

  private readyResolve;
  private readyReject;

  public readonly ready = new Promise<void>((res, rej) => {
    this.readyResolve = res;
    this.readyReject = rej;
  });

  public constructor(private sysConfigService: SystemConfigPromiseService) {
    super();

    Promise.all([this.sysConfigService.isPrimed()]).then(() => {
      const systemPrivateMask = this.sysConfigService.hardGetSystemConfig("PRIVATE_MASK");

      if (typeof systemPrivateMask === "string" && systemPrivateMask.length > 0) {
        this.privateMask = this.sysConfigService.hardGetSystemConfig("PRIVATE_MASK");
      }

      this.readyResolve();
    });
  }

  public asModelPrivatized(label: string, key: string, id: CubiqTS.Long): CubiqTS.UserImpl {
    let user = new CubiqTS.UserImpl();
    user.userId = id;
    user.userCode = label;
    user.userName = label;

    user.extendedInfo.set("__bfx_privatization_key", key);
    return user;
  }

  public asModelRestrictedClientSegment(
    clientSegment: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.UserImpl,
  ): { key: string; value: CubiqTS.UserImpl } {
    let restrictedClientSegment = super.asModelRestrictedClientSegment(
      clientSegment,
      restrictedKey,
      restrictedValue,
    );
    let clientSegmentHash = this.hashClientSegment(clientSegment);
    let key: string;

    if (restrictedClientSegment == null) {
      return null;
    }

    if (clientSegmentHash < 0) {
      // represent negativity
      key = `${restrictedKey.toString()}1${Math.abs(clientSegmentHash)}`;
    } else {
      key = `${restrictedKey.toString()}0${Math.abs(clientSegmentHash)}`;
    }

    restrictedClientSegment.key = key;
    restrictedClientSegment.value.userId = CubiqTS.Long.fromString(key);
    restrictedClientSegment.value.userCode = clientSegment;
    restrictedClientSegment.value.userName = clientSegment;
    restrictedClientSegment.value.extendedInfo.set(
      "__bfx_privatization_key",
      restrictedClientSegment.key,
    );

    return restrictedClientSegment;
  }

  public asModelRestrictedIdName(
    id: string,
    name: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.UserImpl,
  ): { key: string; value: CubiqTS.UserImpl } {
    let idName = `id:${id}_name:${name}`;
    let restrictedClientSegment = super.asModelRestrictedClientSegment(
      idName,
      restrictedKey,
      restrictedValue,
    );
    let idNameHash = this.hashClientSegment(idName);
    let key;

    if (restrictedClientSegment == null) {
      return null;
    }

    if (idNameHash < 0) {
      // represent negativity
      key = CubiqTS.Long.fromString(`${restrictedKey.toString()}10${Math.abs(idNameHash)}`);
    } else {
      let b = new CubiqTS.Long(Math.abs(idNameHash), 1);
      key = CubiqTS.Long.fromString(`${restrictedKey.toString()}00${Math.abs(idNameHash)}`);
    }

    restrictedClientSegment.key = key;
    restrictedClientSegment.value.userId = key;
    restrictedClientSegment.value.userCode = id;
    restrictedClientSegment.value.userName = name;
    restrictedClientSegment.value.extendedInfo.set(
      "__bfx_privatization_key",
      restrictedClientSegment.key.toString(),
    );

    return restrictedClientSegment;
  }

  public getPrivate(): Map<string, CubiqTS.UserImpl> {
    let users = new Map<string, CubiqTS.UserImpl>();
    let user = this.asModelPrivatized(
      this.getPrivateLabel(),
      this.getPrivateKey(),
      this.getPrivateId(),
    );

    StaticDataPrivatizationType.setPrivatization(user, StaticDataPrivatizationType.Private);

    users.set(this.getPrivateKey(), user);

    return users;
  }

  public getPrivateKey(): string {
    return this.getPrivateId().toString();
  }

  getBranchPrivateId(branchCd: string): CubiqTS.Long {
    return this.branchCdIdMap.get(branchCd);
  }

  getPrivateId(): CubiqTS.Long {
    return PRIVATE_ID;
  }

  public getBranchPrivate(branchCd: string): Map<string, CubiqTS.UserImpl> {
    let users = new Map<string, CubiqTS.UserImpl>();
    let id = CubiqTS.Long.fromInt(this.nextBranchCdId);
    let user = this.asModelPrivatized(this.getBranchPrivateLabel(branchCd), id.toString(), id);

    StaticDataPrivatizationType.setPrivatization(user, StaticDataPrivatizationType.BranchPrivate);

    if (this.nextBranchCdId == Number.MIN_SAFE_INTEGER) {
      throw new Error("Too many branches configured");
    }

    users.set(CubiqTS.Long.fromInt(this.nextBranchCdId).toString(), user);
    this.branchCdIdMap.set(branchCd, CubiqTS.Long.fromInt(this.nextBranchCdId));
    this.nextBranchCdId = this.nextBranchCdId - 1;

    return users;
  }

  public getBranchPrivateKey(branchCd: string): string {
    return this.getBranchPrivateId(branchCd)?.toString();
  }

  public getRestricted(): Map<string, CubiqTS.UserImpl> {
    let users = new Map<string, CubiqTS.UserImpl>();
    let user = this.asModelPrivatized(
      this.getRestrictedLabel(),
      this.getRestrictedKey(),
      this.getRestrictedId(),
    );

    StaticDataPrivatizationType.setPrivatization(user, StaticDataPrivatizationType.BranchPrivate);

    users.set(RESTRICTED_ID.toString(), user);

    return users;
  }

  getRestrictedId(): CubiqTS.Long {
    return RESTRICTED_ID;
  }

  public getRestrictedKey(): string {
    return this.getRestrictedId().toString();
  }
}
